/**
 * Design Library — selection and composition rules.
 *
 * `selectPatterns` ranks the catalog for a brief deterministically (ties broken
 * by id, so the same criteria always yield the same order). `validateComposition`
 * applies the pattern composition rules — conflicts and dependencies — to a
 * chosen set.
 *
 * The Thinking Engine uses these to select patterns; OpenAI later consumes the
 * selected patterns; the compiler never knows they exist.
 */

import { ALL_PATTERNS, getPattern } from './registry';

/** Scoring weights — how strongly each criterion pulls a pattern up the list. */
export const SELECTION_WEIGHTS = Object.freeze({
  genome: 5,
  document: 4,
  industry: 3,
  capability: 2,
  audience: 2,
  family: 2,
  tag: 1,
});

const has = (list, value) =>
  Array.isArray(list) && typeof value === 'string' &&
  list.some((x) => String(x).toLowerCase() === value.toLowerCase());

/**
 * Score one pattern against selection criteria. Deterministic.
 * @returns {number}
 */
export const scorePattern = (pattern, criteria = {}) => {
  const w = SELECTION_WEIGHTS;
  let score = 0;
  if (criteria.genome && has(pattern.idealGenomes, criteria.genome)) score += w.genome;
  if (criteria.documentType && has(pattern.idealDocuments, criteria.documentType)) score += w.document;
  if (criteria.industry && has(pattern.idealIndustries, criteria.industry)) score += w.industry;
  if (criteria.audience && has(pattern.idealAudiences, criteria.audience)) score += w.audience;
  if (criteria.family && pattern.family === criteria.family) score += w.family;
  (Array.isArray(criteria.capabilities) ? criteria.capabilities : []).forEach((c) => {
    if (has(pattern.capabilities, c)) score += w.capability;
  });
  (Array.isArray(criteria.tags) ? criteria.tags : []).forEach((t) => {
    if (has(pattern.tags, t)) score += w.tag;
  });
  return score;
};

/**
 * Rank patterns for a brief.
 * @param {Object} criteria { documentType, industry, audience, genome, family, capabilities[], tags[] }
 * @param {Object} [options] { limit, minScore = 1, excludeVariants = false }
 * @returns {{ id: string, score: number, pattern: Object }[]} ranked, most relevant first.
 */
export const selectPatterns = (criteria = {}, { limit, minScore = 1, excludeVariants = false } = {}) => {
  const ranked = ALL_PATTERNS
    .filter((p) => (excludeVariants ? p.variantOf === null : true))
    .map((pattern) => ({ id: pattern.id, score: scorePattern(pattern, criteria), pattern }))
    .filter((r) => r.score >= minScore)
    // Deterministic: score desc, then id asc.
    .sort((a, b) => (b.score - a.score) || a.id.localeCompare(b.id));

  return typeof limit === 'number' ? ranked.slice(0, Math.max(0, limit)) : ranked;
};

/**
 * Apply the composition rules to a chosen set of patterns.
 * @param {string[]} patternIds
 * @returns {{ valid: boolean, unknown: string[], conflicts: Array, missingDependencies: Array }}
 */
export const validateComposition = (patternIds = []) => {
  const ids = Array.isArray(patternIds) ? patternIds : [];
  const unknown = ids.filter((id) => !getPattern(id));
  const known = ids.filter((id) => getPattern(id));
  const present = new Set(known);

  const conflicts = [];
  known.forEach((id) => {
    (getPattern(id).incompatiblePatterns || []).forEach((other) => {
      if (present.has(other)) {
        // Record each conflicting pair once.
        const pair = [id, other].sort();
        if (!conflicts.some((c) => c.pair[0] === pair[0] && c.pair[1] === pair[1])) conflicts.push({ pair });
      }
    });
  });

  const missingDependencies = [];
  known.forEach((id) => {
    (getPattern(id).dependencies || []).forEach((dep) => {
      if (!present.has(dep)) missingDependencies.push({ pattern: id, requires: dep });
    });
  });

  return {
    valid: unknown.length === 0 && conflicts.length === 0 && missingDependencies.length === 0,
    unknown,
    conflicts,
    missingDependencies,
  };
};
